import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../services/api";
import "./IntegrationsPage.css";

const IntegrationsPage = () => {
  const { id: businessId } = useParams();
  const navigate = useNavigate();

  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState("");
  const [selectedCompanyCode, setSelectedCompanyCode] = useState("");

  const [clientId, setClientId] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [storeInput, setStoreInput] = useState("");
  const [storeNumbers, setStoreNumbers] = useState([]);

  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [connecting, setConnecting] = useState(false);
  const [enablingBusiness, setEnablingBusiness] = useState(false);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");

  const [userIsSubway, setUserIsSubway] = useState(0);
  const [businessIsSubway, setBusinessIsSubway] = useState(0);
  const [businessStoreNumber, setBusinessStoreNumber] = useState("");
  const [savedStoreNumbers, setSavedStoreNumbers] = useState([]);

  const [showEnablePrompt, setShowEnablePrompt] = useState(false);
  const [showStorePopup, setShowStorePopup] = useState(false);
  const [popupStoreNumber, setPopupStoreNumber] = useState("");

  console.log("IntegrationsPage useParams businessId:", businessId);

  const fetchCompanies = useCallback(async () => {
    try {
      setLoadingCompanies(true);

      const response = await api.get("/integration-companies");
      const rows = Array.isArray(response.data) ? response.data : [];

    

      setCompanies(rows);

      const subwayCompany = rows.find((company) => company.code === "subway");
      if (subwayCompany) {
        setSelectedCompanyId(String(subwayCompany.id));
      }
    } catch (err) {
      console.error("Error loading integration companies:", err.response?.data || err.message);
      setMessage("Failed to load integration companies.");
      setMessageType("error");
    } finally {
      setLoadingCompanies(false);
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!businessId || businessId === "undefined") {
      setLoadingStatus(false);
      setMessage("Business ID is missing.");
      setMessageType("error");
      return;
    }

    try {
      setLoadingStatus(true);

      

      const response = await api.get(`/integrations/status/${businessId}`);
      const data = response.data || {};

  

      setUserIsSubway(Number(data.user_is_subway || 0));
      setBusinessIsSubway(Number(data.business_is_subway || 0));
      setBusinessStoreNumber(data.business_store_number || "");
      setSavedStoreNumbers(Array.isArray(data.saved_store_numbers) ? data.saved_store_numbers : []);

      if (Number(data.user_is_subway || 0) === 1 && Number(data.business_is_subway || 0) === 0) {
        setShowEnablePrompt(true);
      } else {
        setShowEnablePrompt(false);
      }
    } catch (err) {
      console.error("Error loading integration status:", err.response?.data || err.message);
      setMessage(err.response?.data?.message || "Failed to load integration status.");
      setMessageType("error");
    } finally {
      setLoadingStatus(false);
    }
  }, [businessId]);

  useEffect(() => {
    fetchCompanies();

    if (!businessId || businessId === "undefined") {
      console.log("businessId missing in useEffect:", businessId);
      setLoadingStatus(false);
      setMessage("Business ID is missing.");
      setMessageType("error");
      return;
    }

    fetchStatus();
  }, [businessId, fetchCompanies, fetchStatus]);

  const selectedCompany = useMemo(() => {
    return companies.find((c) => String(c.id) === String(selectedCompanyId)) || null;
  }, [companies, selectedCompanyId]);

  useEffect(() => {
    if (selectedCompany) {
      setSelectedCompanyCode(selectedCompany.code || "");
    } else {
      setSelectedCompanyCode("");
    }
  }, [selectedCompany]);

  const addStoreNumber = () => {
    const cleaned = String(storeInput || "").trim();

    if (!cleaned) {
      return;
    }

    if (storeNumbers.includes(cleaned)) {
      setMessage("That store number has already been added.");
      setMessageType("error");
      return;
    }

    setStoreNumbers((prev) => [...prev, cleaned]);
    setStoreInput("");
    setMessage("");
    setMessageType("");
  };

  const removeStoreNumber = (storeToRemove) => {
    setStoreNumbers((prev) => prev.filter((store) => store !== storeToRemove));
  };

  const handleStoreInputKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addStoreNumber();
    }
  };

  const validateConnectForm = () => {
    if (!selectedCompanyId) {
      setMessage("Please select a company.");
      setMessageType("error");
      return false;
    }

    if (!clientId.trim()) {
      setMessage("Client ID is required.");
      setMessageType("error");
      return false;
    }

    if (!apiKey.trim()) {
      setMessage("API key is required.");
      setMessageType("error");
      return false;
    }

    if (storeNumbers.length === 0) {
      setMessage("Please add at least one store number.");
      setMessageType("error");
      return false;
    }

    return true;
  };

  const handleConnect = async () => {
    if (!validateConnectForm()) {
      return;
    }

    try {
      setConnecting(true);
      setMessage("");
      setMessageType("");

      const payload = {
        integration_company_id: Number(selectedCompanyId),
        client_id: clientId.trim(),
        api_key: apiKey.trim(),
        store_numbers: storeNumbers
      };

      

      await api.post("/integrations/connect", payload);

      setMessage("Integration connected successfully.");
      setMessageType("success");

      setClientId("");
      setApiKey("");
      setStoreInput("");
      setStoreNumbers([]);

      if (businessId && businessId !== "undefined") {
        await fetchStatus();
      }
    } catch (err) {
      console.error("Error connecting integration:", err.response?.data || err.message);
      setMessage(err.response?.data?.message || "Failed to connect integration.");
      setMessageType("error");
    } finally {
      setConnecting(false);
    }
  };

  const handleYesClick = () => {
    setPopupStoreNumber(savedStoreNumbers[0] || "");
    setShowStorePopup(true);
  };

  const handleNoClick = () => {
    setShowEnablePrompt(false);
  };

  const handleEnableBusiness = async () => {
    if (!businessId || businessId === "undefined") {
      setMessage("Business ID is missing.");
      setMessageType("error");
      return;
    }

    if (!popupStoreNumber) {
      setMessage("Please select a store number.");
      setMessageType("error");
      return;
    }

    try {
      setEnablingBusiness(true);
      setMessage("");
      setMessageType("");

      

      await api.post(`/integrations/enable-business/${businessId}`, {
        store_number: popupStoreNumber
      });

      setShowStorePopup(false);
      setPopupStoreNumber("");
      setMessage("Subway integration enabled for this business.");
      setMessageType("success");

      await fetchStatus();
    } catch (err) {
      console.error("Error enabling business integration:", err.response?.data || err.message);
      setMessage(err.response?.data?.message || "Failed to enable Subway for this business.");
      setMessageType("error");
    } finally {
      setEnablingBusiness(false);
    }
  };

  const renderStorePills = (stores, removable = false) => {
    if (!stores || stores.length === 0) {
      return <div className="integrations-empty">No store numbers added.</div>;
    }

    return (
      <div className="integrations-store-list">
        {stores.map((store) => (
          <div key={store} className="integrations-store-pill">
            <span>{store}</span>
            {removable ? (
              <button
                type="button"
                className="integrations-remove-store-btn"
                onClick={() => removeStoreNumber(store)}
              >
                ×
              </button>
            ) : null}
          </div>
        ))}
      </div>
    );
  };

  const showConnectForm = userIsSubway !== 1;
  const showConnectedView = userIsSubway === 1 && businessIsSubway === 1;

  return (
    <div className="integrations-page">
      <div className="integrations-card">
        <h1 className="pw-page-title">Integrations</h1>

        {(loadingCompanies || loadingStatus) && (
          <div className="integrations-loading">Loading...</div>
        )}

        {!loadingCompanies && !loadingStatus && (
          <>
            {message ? (
              <div
                className={`integrations-message ${
                  messageType === "success"
                    ? "integrations-message-success"
                    : "integrations-message-error"
                }`}
              >
                {message}
              </div>
            ) : null}

            {showConnectForm && (
              <>
                <div className="integrations-grid">
                  <div className="integrations-field">
                    <label className="integrations-label">Company</label>
                    <select
                      className="integrations-input"
                      value={selectedCompanyId}
                      onChange={(e) => setSelectedCompanyId(e.target.value)}
                    >
                      <option value="">Select Company</option>
                      {companies.map((company) => (
                        <option key={company.id} value={company.id}>
                          {company.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {selectedCompanyCode === "subway" && (
                    <>
                      <div className="integrations-field">
                        <label className="integrations-label">Client ID</label>
                        <input
                          type="text"
                          className="integrations-input"
                          value={clientId}
                          onChange={(e) => setClientId(e.target.value)}
                          placeholder="Enter client ID"
                        />
                      </div>

                      <div className="integrations-field">
                        <label className="integrations-label">API Key</label>
                        <input
                          type="text"
                          className="integrations-input"
                          value={apiKey}
                          onChange={(e) => setApiKey(e.target.value)}
                          placeholder="Enter API key"
                        />
                      </div>

                      <div className="integrations-field integrations-field-full">
                        <label className="integrations-label">Add Store Number</label>
                        <div className="integrations-add-store-row">
                          <input
                            type="text"
                            className="integrations-input"
                            value={storeInput}
                            onChange={(e) => setStoreInput(e.target.value)}
                            onKeyDown={handleStoreInputKeyDown}
                            placeholder="Enter store number"
                          />
                          <button
                            type="button"
                            className="integrations-add-btn"
                            onClick={addStoreNumber}
                          >
                            Add
                          </button>
                        </div>
                      </div>

                      <div className="integrations-field integrations-field-full">
                        <label className="integrations-label">Store Numbers</label>
                        {renderStorePills(storeNumbers, true)}
                      </div>
                    </>
                  )}
                </div>

                <div className="integrations-actions">
                  <button
                    type="button"
                    className="integrations-connect-btn"
                    onClick={handleConnect}
                    disabled={connecting}
                  >
                    {connecting ? "Connecting..." : "Connect"}
                  </button>
                </div>
              </>
            )}

            {!showConnectForm && showEnablePrompt && !showConnectedView && (
              <div className="integrations-status-box">
                <h2 className="integrations-status-title">Turn integration on?</h2>

                <div className="integrations-actions">
                  <button
                    type="button"
                    className="integrations-yes-btn"
                    onClick={handleYesClick}
                  >
                    Yes
                  </button>
                  <button
                    type="button"
                    className="integrations-no-btn"
                    onClick={handleNoClick}
                  >
                    No
                  </button>
                </div>

                {savedStoreNumbers.length > 0 && (
                  <div className="integrations-saved-stores-wrap">
                    <label className="integrations-label">Saved Store Numbers</label>
                    {renderStorePills(savedStoreNumbers, false)}
                  </div>
                )}
              </div>
            )}

            {showConnectedView && (
  <div className="integrations-status-box">
    <h2 className="integrations-connected-text">Connected to Subway</h2>

    {businessStoreNumber ? (
      <p className="integrations-connected-subtext">
        Store Number: <strong>{businessStoreNumber}</strong>
      </p>
    ) : null}

    <div className="integrations-actions">
      <button
        type="button"
        className="avg-cogs-btn"
        onClick={() => navigate(`/dashboard/business/${businessId}/subway-data`)}
      >
        Subway Data
      </button>

      {/* <button
        type="button"
        className="avg-cogs-btn"
        onClick={() => navigate(`/dashboard/business/${businessId}/subway-cash-in`)}
      >
        Subway Cash in
      </button> */}

      <button
        type="button"
        className="avg-cogs-btn"
        onClick={() => navigate(`/dashboard/business/${businessId}/subway-required`)}
      >
        Cash Reconciliation
      </button>

      <button
  type="button"
  className="avg-cogs-btn"
  onClick={() => navigate(`/dashboard/business/${businessId}/subway-paidouts`)}
>
  Paidouts
</button>

      <button
  type="button"
  className="avg-cogs-btn"
  onClick={() => navigate(`/dashboard/business/${businessId}/subway-spotcheck`)}
>
  Spot Check
</button>
    </div>
  </div>
)}
          </>
        )}
      </div>

      {showStorePopup && (
        <div className="integrations-popup-overlay">
          <div className="integrations-popup">
            <h3 className="integrations-popup-title">Select Store Number</h3>

            <div className="integrations-field">
              <label className="integrations-label">Store Number</label>
              <select
                className="integrations-input"
                value={popupStoreNumber}
                onChange={(e) => setPopupStoreNumber(e.target.value)}
              >
                <option value="">Select Store Number</option>
                {savedStoreNumbers.map((store) => (
                  <option key={store} value={store}>
                    {store}
                  </option>
                ))}
              </select>
            </div>

            <div className="integrations-actions">
              <button
                type="button"
                className="integrations-connect-btn"
                onClick={handleEnableBusiness}
                disabled={enablingBusiness}
              >
                {enablingBusiness ? "Saving..." : "Submit"}
              </button>

              <button
                type="button"
                className="integrations-no-btn"
                onClick={() => {
                  setShowStorePopup(false);
                  setPopupStoreNumber("");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IntegrationsPage;